import type { Container, Sprite } from 'pixi.js';
import { clamp } from '../../shared/scalar';
import type { Shape } from '../paint';
import { add, hide, setTint } from '../util';
import { SCALE_RADIUS, UNIT } from './tatoue-paint';

const SCALES = 12;
const WIDTH = { head: 5.5, tail: 1.4, rim: 0.8 };
const LEGS_AT = [2, 7] as const;
const GLOW_RADIUS = 12;
const GLOW_ALPHA = 0.5;

export interface DragonShapes {
  readonly scale: Shape;
  readonly dragonHead: Shape;
  readonly dragonEyes: Shape;
  readonly dragonLegs: Shape;
  readonly dragonTuft: Shape;
}

export interface DragonView {
  readonly glow: Sprite;
  readonly legs: readonly Sprite[];
  readonly tuft: Sprite;
  readonly scales: readonly Sprite[];
  readonly head: Sprite;
  readonly eyes: Sprite;
}

export interface Point {
  x: number;
  y: number;
}

export type DragonPath = (back: number, out: Point) => void;

export function createDragonView(
  bodies: Container,
  glows: Container,
  shapes: DragonShapes,
  halo: Shape,
): DragonView {
  const glow = add(glows, halo);
  const legs = LEGS_AT.map(() => add(bodies, shapes.dragonLegs));
  const tuft = add(bodies, shapes.dragonTuft);
  const scales = Array.from({ length: SCALES }, () => add(bodies, shapes.scale));
  scales.reverse();
  const head = add(bodies, shapes.dragonHead);
  return { glow, legs, tuft, scales, head, eyes: add(bodies, shapes.dragonEyes) };
}

export function hideDragon(view: DragonView): void {
  hide(view.glow, view.tuft, view.head, view.eyes, ...view.legs, ...view.scales);
}

const from: Point = { x: 0, y: 0 };
const to: Point = { x: 0, y: 0 };

function along(sprite: Sprite, path: DragonPath, back: number, size: number, alpha: number) {
  path(back, to);
  path(back + 0.85, from);
  sprite.visible = true;
  sprite.position.set(to.x, to.y);
  sprite.rotation = Math.atan2(to.y - from.y, to.x - from.x);
  sprite.scale.set(size);
  sprite.alpha = alpha;
}

export function placeDragon(
  view: DragonView,
  path: DragonPath,
  out: number,
  color: number,
  unit: number,
  alpha: number,
  glow: number,
  halo: Shape,
): void {
  const size = unit / UNIT;
  for (let index = 0; index < SCALES; index += 1) {
    const sprite = view.scales[index];
    if (sprite === undefined) {
      continue;
    }
    if (index + 1 > out) {
      sprite.visible = false;
      continue;
    }
    path(index, to);
    path(index + 1, from);
    const width = WIDTH.head + ((WIDTH.tail - WIDTH.head) * index) / SCALES;
    sprite.visible = true;
    sprite.position.set((from.x + to.x) / 2, (from.y + to.y) / 2);
    sprite.scale.set((size * (width / 2 + WIDTH.rim)) / SCALE_RADIUS);
    sprite.alpha = alpha;
    setTint(sprite, color);
  }
  view.legs.forEach((legs, index) => {
    const at = LEGS_AT[index] ?? 0;
    along(legs, path, at, size, alpha);
    legs.visible = at + 1 <= out;
    setTint(legs, color);
  });
  along(view.tuft, path, clamp(Math.floor(out), 0, SCALES), size, alpha);
  view.tuft.visible = out >= 2;
  setTint(view.tuft, color);
  along(view.head, path, 0, size, alpha);
  setTint(view.head, color);
  along(view.eyes, path, 0, size, alpha);
  path(0, to);
  view.glow.visible = glow > 0;
  view.glow.position.set(to.x, to.y);
  view.glow.scale.set((GLOW_RADIUS * unit) / halo.radius);
  view.glow.alpha = GLOW_ALPHA * glow * alpha;
  setTint(view.glow, color);
}
